import { Checkbox, Tooltip } from '@atgfr8/atg-ui-tailwind';
import { MenuDownIcon, MenuUpIcon } from '@atgfr8/icons';
import {
  createEffect,
  createSignal,
  For,
  JSX,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import './Styles.css';

export type SelectInputCustomMenuItem = {
  label: string | JSX.Element;
  value: string;
  disabled?: boolean;
  class?: string;
  title?: string;
};

export enum SelectInputCustomOnChangeActionType {
  Select = 'select',
  SelectAll = 'selectAll',
  Deselect = 'deselect',
  DeselectAll = 'deselectAll',
}

export type SelectInputCustomProps = {
  label?: string;
  menuItems: SelectInputCustomMenuItem[];
  value?: string | string[];
  multiple?: boolean;
  onChange?: (
    value: string,
    action: SelectInputCustomOnChangeActionType,
  ) => void;
  onBlur?: () => void;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  error?: string | null;
  style?: JSX.CSSProperties;
  required?: boolean;
  dataTestId: string;
  size?: 'sm' | 'md' | 'lg';
  class?: string;
  menuClass?: string;
  menuItemClass?: string;
  renderValue?: (
    value: string | string[],
    item?: SelectInputCustomMenuItem,
  ) => JSX.Element | string;
  iconColor?: string;
  showCheckboxes?: boolean;
  selectAllOption?: boolean;
};

export const SelectInputCustom = (props: SelectInputCustomProps) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const [activeIndex, setActiveIndex] = createSignal(-1);
  const [menuStyle, setMenuStyle] = createSignal<JSX.CSSProperties>({});

  const MENU_GAP = 4;
  const MIN_MENU_HEIGHT = 120;

  let containerRef: HTMLDivElement | undefined;
  let triggerRef: HTMLButtonElement | undefined;
  let listboxRef: HTMLUListElement | undefined;

  const TYPE_AHEAD_RESET_MS = 500;
  let typeAheadBuffer = '';
  let typeAheadTimer: ReturnType<typeof setTimeout> | undefined;

  const hasError = () => Boolean(props.error);

  const heightClass = () => {
    if (props.multiple) return 'min-h-[40px] h-auto';
    if (props.size === 'sm') return 'h-[32px] text-xs';
    if (props.size === 'md') return 'h-[40px]';
    if (props.size === 'lg') return 'h-[48px]';
    return 'h-[36px]';
  };

  const selectedValues = (): string[] => {
    if (!props.multiple) {
      return [];
    }

    return Array.isArray(props.value) ? props.value : [];
  };

  const singleValue = () => {
    if (props.multiple || Array.isArray(props.value)) {
      return '';
    }

    return props.value ?? '';
  };

  const hasValue = () => {
    if (props.disabled) return false;
    if (props.multiple) return selectedValues().length > 0;
    return singleValue() !== '';
  };

  const selectedItem = () =>
    props.menuItems.find((item) => item.value === singleValue());

  const enabledItems = () => props.menuItems.filter((item) => !item.disabled);

  const lastEnabledIndex = () => enabledItems().length - 1;

  const close = (shouldBlur = false) => {
    if (!isOpen()) return;
    setIsOpen(false);
    setActiveIndex(-1);
    setMenuStyle({});
    if (shouldBlur) triggerRef?.blur();
  };

  const updateMenuPlacement = () => {
    if (!triggerRef) return;

    const rect = triggerRef.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const openUpward = spaceBelow < spaceAbove;
    const maxHeight = Math.min(
      window.innerHeight * 0.8,
      Math.max(MIN_MENU_HEIGHT, openUpward ? spaceAbove : spaceBelow),
    );

    setMenuStyle({
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      top: openUpward ? undefined : `${rect.bottom + MENU_GAP}px`,
      bottom: openUpward
        ? `${window.innerHeight - rect.top + MENU_GAP}px`
        : undefined,
      'max-height': `${maxHeight}px`,
      'z-index': 1300,
    });
  };

  const open = () => {
    if (props.disabled) return;
    updateMenuPlacement();
    setIsOpen(true);
    const selected = selectedItem();
    if (selected) {
      const index = enabledItems().findIndex(
        (item) => item.value === selected.value,
      );
      setActiveIndex(index >= 0 ? index : 0);
      return;
    }
    setActiveIndex(enabledItems().length > 0 ? 0 : -1);
  };

  const toggle = () => {
    if (isOpen()) {
      close();
      return;
    }
    open();
  };

  const selectValue = (
    value: string,
    action: SelectInputCustomOnChangeActionType = SelectInputCustomOnChangeActionType.Select,
  ) => {
    props.onChange?.(value, action);
    if (!props.multiple) {
      close();
    }
  };

  const displayValue = () => {
    if (props.multiple) {
      const values = selectedValues();
      if (values.length === 0) return props.placeholder ?? '';
      if (props.renderValue) return props.renderValue(values);
      return values.join(', ');
    }

    const value = singleValue();
    if (props.renderValue) return props.renderValue(value, selectedItem());
    if (!value) return props.placeholder ?? '';
    const item = selectedItem();
    if (item && typeof item.label === 'string') return item.label;
    return value;
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (containerRef?.contains(target) || listboxRef?.contains(target)) {
      return;
    }
    close();
  };

  const moveActiveIndex = (direction: 1 | -1) => {
    const items = enabledItems();
    if (items.length === 0) return;

    setActiveIndex((prev) => {
      const start =
        prev < 0 ? (direction === 1 ? 0 : lastEnabledIndex()) : prev;
      let next = start;
      for (let i = 0; i < items.length; i += 1) {
        next =
          direction === 1
            ? next >= lastEnabledIndex()
              ? 0
              : next + 1
            : next <= 0
              ? lastEnabledIndex()
              : next - 1;
        if (!items[next]?.disabled) return next;
      }
      return prev;
    });
  };

  const itemText = (item: SelectInputCustomMenuItem): string =>
    typeof item.label === 'string' ? item.label : item.value;

  // Type-ahead: jump the active option to the first item whose label starts
  // with the typed characters, matching the old SUID Select behaviour. Pressing
  // the same key repeatedly cycles through items sharing that first letter.
  const moveActiveToTypedChar = (char: string) => {
    const items = enabledItems();
    if (items.length === 0) return;

    const lowerChar = char.toLowerCase();
    // A repeat is the same single character pressed again before the buffer
    // resets — only then do we cycle to the *next* match.
    const isRepeat =
      typeAheadBuffer.length > 0 &&
      typeAheadBuffer.split('').every((c) => c === lowerChar);

    if (typeAheadTimer) clearTimeout(typeAheadTimer);
    typeAheadTimer = setTimeout(() => {
      typeAheadBuffer = '';
    }, TYPE_AHEAD_RESET_MS);

    typeAheadBuffer += lowerChar;
    const search = typeAheadBuffer;
    // For a run of the same character, match on just that character (cycling);
    // otherwise match the full typed string.
    const allSameChar = search.split('').every((c) => c === search[0]);
    const needle = allSameChar ? search[0]! : search;
    const matches = (item: SelectInputCustomMenuItem) =>
      itemText(item).trim().toLowerCase().startsWith(needle);

    // First press → search from the current option (inclusive) so we land on the
    // first match. Repeat press → start after the current to advance the cycle.
    const current = activeIndex();
    const start = isRepeat
      ? current < 0
        ? 0
        : current + 1
      : Math.max(current, 0);

    for (let i = 0; i < items.length; i += 1) {
      const idx = (start + i) % items.length;
      if (matches(items[idx]!)) {
        setActiveIndex(idx);
        return;
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (props.disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen()) {
          open();
          return;
        }
        moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen()) {
          open();
          return;
        }
        moveActiveIndex(-1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen()) {
          open();
          return;
        }
        {
          const idx = activeIndex();
          if (idx < 0) break;
          selectValue(enabledItems()[idx]!.value);
        }
        break;
      case 'Escape':
        if (isOpen()) {
          event.preventDefault();
          close(true);
        }
        break;
      case 'Tab':
        close();
        break;
      default:
        if (
          event.key.length === 1 &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey &&
          /\S/.test(event.key)
        ) {
          event.preventDefault();
          if (!isOpen()) open();
          moveActiveToTypedChar(event.key);
        }
        break;
    }
  };

  createEffect(() => {
    if (!isOpen()) return;

    const handleReposition = () => updateMenuPlacement();

    requestAnimationFrame(handleReposition);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    // Reposition when the trigger itself changes size — e.g. multi-select chips
    // wrapping to a new row grows the field height — so the menu keeps tracking
    // the trigger's bottom edge instead of overlapping it.
    let triggerResizeObserver: ResizeObserver | undefined;
    if (triggerRef && typeof ResizeObserver !== 'undefined') {
      triggerResizeObserver = new ResizeObserver(handleReposition);
      triggerResizeObserver.observe(triggerRef);
    }

    onCleanup(() => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      triggerResizeObserver?.disconnect();
    });
  });

  createEffect(() => {
    const idx = activeIndex();
    const items = enabledItems();
    if (!isOpen() || idx < 0 || idx >= items.length || !listboxRef) return;
    const el = listboxRef.querySelector(
      `[data-option-value="${items[idx].value}"]`,
    ) as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  });

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
  });

  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    if (typeAheadTimer) clearTimeout(typeAheadTimer);
  });

  const menuContainerClass = () => 'overflow-y-auto';

  return (
    <div ref={containerRef} class="relative w-full min-w-0">
      <Show when={props.name && !props.multiple}>
        <input
          type="hidden"
          name={props.name}
          value={singleValue()}
          disabled={props.disabled}
          required={(props.required ?? false) && !props.disabled}
        />
      </Show>

      <button
        ref={triggerRef}
        type="button"
        style={props.style ?? undefined}
        data-testid={props.dataTestId}
        disabled={props.disabled}
        aria-label={props.label || undefined}
        aria-required={props.required || undefined}
        aria-invalid={hasError() || undefined}
        aria-haspopup="listbox"
        aria-expanded={isOpen()}
        class={`select-input-custom-trigger cursor-pointer peer relative w-full pl-3 pr-10 py-2 border rounded-sm shadow-xs
          focus:outline-hidden disabled:border-gray-300 text-left flex items-center
          ${heightClass()}
          ${props.multiple ? 'items-start' : ''}
          ${props.disabled ? 'select-input-disabled' : ''}
          ${
            hasError() && !props.disabled
              ? 'border-error-red'
              : 'border-input-border hover:border-armstrong-primary focus:border-armstrong-primary'
          }
          ${hasValue() ? 'select-input-has-value' : ''}
          ${props.class ?? ''}`}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          const activeElement = document.activeElement;
          if (
            !containerRef?.contains(activeElement) &&
            !listboxRef?.contains(activeElement)
          ) {
            props.onBlur?.();
          }
        }}
      >
        <span
          class={`display-value flex-1 min-w-0 ${props.multiple ? '' : 'truncate'} ${
            hasValue() ? 'text-[#374151]' : 'text-[#9e9e9e]'
          }`}
        >
          {displayValue()}
        </span>
        <Show
          when={isOpen()}
          fallback={
            <MenuDownIcon
              size="18px"
              color={props.iconColor ?? '#757575'}
              class="pointer-events-none absolute right-3 top-1/2 shrink-0 -translate-y-1/2"
            />
          }
        >
          <MenuUpIcon
            size="18px"
            color={props.iconColor ?? '#757575'}
            class="pointer-events-none absolute right-3 top-1/2 shrink-0 -translate-y-1/2"
          />
        </Show>
      </button>

      <Show when={isOpen()}>
        <Portal mount={document.body}>
          <ul
            ref={listboxRef}
            role="listbox"
            aria-multiselectable={props.multiple || undefined}
            style={menuStyle()}
            onMouseLeave={() => setActiveIndex(-1)}
            class={`select-input-custom-menu rounded-sm list-none m-0 p-0 ${menuContainerClass()} ${
              props.menuClass ?? ''
            }`}
          >
            <Show when={props.selectAllOption && props.multiple}>
              <li
                role="option"
                data-option-value="__select_all__"
                aria-selected={
                  (props.value?.length ?? 0) === enabledItems().length
                }
                class={`select-input-custom-option ${
                  selectedValues().length === enabledItems().length
                    ? 'select-input-custom-option--selected'
                    : ''
                } ${props.menuItemClass ?? ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (selectedValues().length === enabledItems().length) {
                    selectValue(
                      '__deselect_all__',
                      SelectInputCustomOnChangeActionType.DeselectAll,
                    );
                  } else {
                    selectValue(
                      '__select_all__',
                      SelectInputCustomOnChangeActionType.SelectAll,
                    );
                  }
                }}
              >
                <Show when={props.showCheckboxes}>
                  <Checkbox
                    size="xs"
                    checked={selectedValues().length === enabledItems().length}
                    dataTestId="select-input-custom-select-all"
                  />
                </Show>
                <span>Select All</span>
              </li>
            </Show>
            <For each={props.menuItems}>
              {(item) => {
                const enabledIndex = () =>
                  enabledItems().findIndex(
                    (enabledItem) => enabledItem.value === item.value,
                  );
                const isActive = () =>
                  !item.disabled && enabledIndex() === activeIndex();
                const isSelected = () => {
                  if (props.multiple) {
                    return selectedValues().includes(item.value);
                  }

                  return item.value === singleValue();
                };

                return (
                  <li
                    role="option"
                    data-option-value={item.value}
                    aria-selected={isSelected()}
                    aria-disabled={item.disabled || undefined}
                    class={`select-input-custom-option ${
                      item.disabled
                        ? 'select-input-custom-option--disabled'
                        : ''
                    } ${isActive() ? 'select-input-custom-option--active' : ''} ${
                      isSelected() ? 'select-input-custom-option--selected' : ''
                    } ${props.menuItemClass ?? ''} ${item.class ?? ''}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => {
                      setActiveIndex(item.disabled ? -1 : enabledIndex());
                    }}
                    onClick={() => {
                      if (!item.disabled) {
                        let action: SelectInputCustomOnChangeActionType =
                          SelectInputCustomOnChangeActionType.Select;

                        if (
                          props.multiple &&
                          props.value &&
                          Array.isArray(props.value) &&
                          props.value.includes(item.value)
                        ) {
                          action = SelectInputCustomOnChangeActionType.Deselect;
                        }

                        selectValue(item.value, action);
                      }
                    }}
                  >
                    <Show when={props.showCheckboxes && props.multiple}>
                      <Checkbox
                        size="xs"
                        checked={isSelected()}
                        disabled={item.disabled}
                        dataTestId={`select-input-custom-option-${item.value}`}
                      />
                    </Show>
                    <Show
                      when={item.title}
                      fallback={<span class="w-full">{item.label}</span>}
                    >
                      <Tooltip
                        title={item.title!}
                        class="w-full"
                        placement="right"
                        dataTestId={`${props.dataTestId}-option-tooltip-${item.value}`}
                      >
                        <span>{item.label}</span>
                      </Tooltip>
                    </Show>
                  </li>
                );
              }}
            </For>
          </ul>
        </Portal>
      </Show>

      <Show when={props.label}>
        <label
          class={`select-input-label absolute -top-2 left-2 px-1 text-xs font-medium pointer-events-none
            overflow-hidden whitespace-nowrap text-ellipsis max-w-[90%] ${
              hasError() ? 'text-error-red' : 'text-armstrong-primary'
            }`}
        >
          {props.label}
          {props.required && <span> *</span>}
        </label>
      </Show>

      <Show when={hasError()}>
        <div class="text-error-red text-xs mt-1 px-2">{props.error}</div>
      </Show>
    </div>
  );
};
